import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GeocodeResult {
  postalCode: string | null;
  addressLocality: string | null;
  addressRegion: string | null;
}

// In-memory cache for geocode results
const geocodeCache = new Map<string, GeocodeResult>();

export const useReverseGeocode = (latitude?: number | null, longitude?: number | null) => {
  const [data, setData] = useState<GeocodeResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!latitude || !longitude) {
      setData(null);
      return;
    }

    const cacheKey = `${latitude},${longitude}`;
    
    // Check cache first
    const cached = geocodeCache.get(cacheKey);
    if (cached) {
      setData(cached);
      return;
    }

    const fetchGeocode = async () => {
      setIsLoading(true);
      try {
        const { data: result, error } = await supabase.functions.invoke('mapbox-reverse-geocode', {
          body: { latitude, longitude }
        });

        if (error) {
          console.error('Reverse geocode error:', error);
          return;
        }

        const geocodeData: GeocodeResult = {
          postalCode: result.postalCode,
          addressLocality: result.addressLocality,
          addressRegion: result.addressRegion
        };

        // Cache the result
        geocodeCache.set(cacheKey, geocodeData);
        setData(geocodeData);
      } catch (error) {
        console.error('Error fetching geocode:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeocode();
  }, [latitude, longitude]);

  return { data, isLoading };
};
